import React from 'react';
import { Card, Empty, Typography, Button, Tooltip, Space } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import styles from './BankSelect.module.css';
import { pluralize } from '@utils/pluralize';
import { SelectBankInfo } from '@services/bankApi';

const TERMS = {
  result: 'Соответствует',
  law: 'Закон',
  bgType: 'Тип БГ',
  bgAmount: 'Сумма БГ',
  duration: 'Срок БГ',
  bgPrincipalOkved: 'ОКВЭД Принципала',
  bgPrincipalRegion: 'Регион Принципала',
  bgBeneficiaryOkved: 'ОКВЭД Бенифициара',
  bgBeneficiaryRegion: 'Регион Бенифициара',
}

export type BankInfo = {
  inn: string;
  name: string;
};

interface Props {
  banks: SelectBankInfo[];
  selectedBanks: Set<string>;
  onSelect: (inn: string | string[]) => void;
}

export default function BankSelect({
  banks,
  selectedBanks,
  onSelect,
}: Props) {
  const reset = () => {
    onSelect([]);
  };

  const selectAll = () => {
    const all = banks.filter(b => Boolean(b.terms.result)).map(b => b.inn);
    onSelect(all);
  };

  const renderCard = (bank: SelectBankInfo) => {
    const active = selectedBanks.has(bank.inn);
    const disabled = !bank.terms.result;

    return (
      <Tooltip key={bank.inn}
        overlayStyle={!disabled ? {display: 'none'} : {}}
        title={() => (
          <Space direction="vertical" size="small">
            {Object.entries(bank.terms).map(([key, value]) => {
              if (key === 'result') return;
              if (value) return;

              return (
                <Space key={key} size="small">
                  <span>{value ? '✅' : '❌'}</span>
                  <span>{TERMS[key] || key}</span>
                </Space>
              );
            })}
          </Space>
        )}
        placement="topLeft"
      >
        <Card
          key={bank.inn}
          size="small"
          hoverable
          onClick={() => {
              if (disabled) return;
              onSelect(bank.inn)
            }
          }
          className={`${styles.card} ${active ? styles.active : styles.inactive} ${disabled && styles.bankDisabled}`}
          bodyStyle={{ display: 'flex', alignItems: 'center', position: 'relative' }}
        >
          <Typography.Text strong ellipsis style={{ width: '100%' }}>
            {bank.name}
          </Typography.Text>
          {active && <CheckOutlined className={styles.checkIcon} />}
        </Card>
      </Tooltip>
    );
  };

  return (
    <div>
      {banks?.length ? (
        <>
          <div className={styles.grid}>{banks.map((b) => renderCard(b))}</div>
          <div className={styles.footer}>
            <Typography.Text type="secondary">
              {pluralize(selectedBanks.size, ['Выбран', 'Выбрано', 'Выбрано'])}{' '}
              {selectedBanks.size}{' '}
              {pluralize(selectedBanks.size, ['банк', 'банка', 'банков'])}
            </Typography.Text>
            {selectedBanks.size ? (
              <Button type="link" size="small" onClick={reset} style={{ marginLeft: 8 }}>Сбросить</Button>
            ) : (
              <Button type="link" size="small" onClick={selectAll} disabled={!banks?.length} style={{ marginLeft: 8 }}>Выбрать все</Button>
            )}
          </div>
        </>
      ) : (
        <Empty description="Нет доступных банков" />
      )}
    </div>
  );
}
